import React, {
  ChangeEvent,
  KeyboardEventHandler,
  useEffect,
  useState,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { AuthSource, loginUser } from "../../services/AuthService";
import {
  apiCreateUserWithWorkspace,
  apiGetUserByEmail,
  apiGetWorkspaceCollections,
} from "../../api";
import {
  apiAuthDeleteUserCodes,
  apiAuthSendVerificationCode,
  apiAuthVerifyCode,
} from "../../services/AuthService";
import { validateEmail, validatePassword } from "../uiHelpers";
import { useDispatch } from "react-redux";
import {
  setEditingCollection,
  setSignUpNow,
} from "../../redux/features/appData";
import "./SignupWidget.css";
import { toast, Toaster } from "react-hot-toast";
import { toastSuccess } from "../../config";
import { Button } from "../../ds2/components";
import config from "environment";

type SignupWidgetProps = {
  hostUrl?: string;
};

export default function SignupWidget({ hostUrl }: SignupWidgetProps) {
  const auth = useAuth();

  const [email, setEmail] = useState<string | undefined>();
  const [emailOK, setEmailOK] = useState<boolean>(false);
  const [password, setPassword] = useState<string>("");
  const [passwordOK, setPasswordOK] = useState<boolean>(false);
  const [code, setCode] = useState<string>("");
  const [codeErrorText, setCodeErrorText] = useState<string>("");
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [errorText, setErrorText] = useState<string>("");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { loginWithRedirect } = useAuth0();

  const handleOAuthSignup = () => {
    dispatch(setSignUpNow(true));
    loginWithRedirect();
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const emailParam = queryParams.get("email");
    setEmail(emailParam ?? "");
  }, []);

  const handleVerifyEmail = async () => {
    if (!validatePassword(password)) {
      setErrorText("Passwords need min 8 chars, a capital and a number.");
      return;
    }

    if (firstName?.length < 1 || lastName?.length < 1) {
      setErrorText("Please enter a first and last name.");
      return;
    }

    if (email) {
      // fire off a verification code and then turn on the screen
      await apiAuthSendVerificationCode(email);

      setCode("");
      setPasswordOK(true);
    }
  };

  const handleDirectSignup = async () => {
    const user = await apiCreateUserWithWorkspace(
      firstName,
      lastName,
      email,
      null,
      password
    );

    // const newState = !hostUrl
    //   ? ONBOARDING_STATE.front
    //   : ONBOARDING_STATE.annotator;
    // dispatch(setOnboardingState(newState));

    if (!user) {
      setErrorText("Problem creating user. Please try again.");
      return;
    }

    const collections = await apiGetWorkspaceCollections(
      user.currentWorkspaceId
    );

    if (!collections || collections.length === 0) {
      setErrorText("Problem creating collection. Please try again.");
      return;
    }

    dispatch(setEditingCollection(collections[0]));

    if (email) {
      auth.login(
        {
          email,
          passwordKey: password,
          authSource: AuthSource.STANDARD,
        },
        undefined
      );
    }
  };

  const handlePassword = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setPassword(value);

    validatePassword(value)
      ? setErrorText("")
      : setErrorText("Passwords need min 8 chars, a capital and a number.");
  };

  const handleCode = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    // ignore anything over 6 chars
    if (value && value.length > 6) {
      return;
    }

    setCode(value);
  };

  const handleResend = async () => {
    if (email) {
      const result = await apiAuthSendVerificationCode(email);
      toast("New code sent.", toastSuccess);
    }
  };

  const handleVerifyCode = async () => {
    setCodeErrorText("");

    if (email && code.length === 6) {
      const result = await apiAuthVerifyCode(email, code);

      if (!result) {
        setCodeErrorText("Invalid or expired code.");
        return;
      }

      // clean up all this code madness
      await apiAuthDeleteUserCodes(email);
      handleDirectSignup();
    }
  };

  // verify the email is not being used in the system
  const submitEmail = async () => {
    if (!validateEmail(email)) {
      setErrorText("Please enter a valid email.");
      return;
    }

    const user = await apiGetUserByEmail(email);

    if (user) {
      setErrorText("That email is already in use.");
      return;
    }

    setEmailOK(true);
  };

  return (
    <>
      <div className="signup-widget-inside">
        {!emailOK && (
          <>
            {/* <div className="signup-widget-email">
              <input
                type="text"
                placeholder="Enter your email"
                value={email}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  const value = event.target.value;
                  setEmail(value);
                }}
                onKeyDown={(e: any) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    submitEmail();
                  }
                  e.stopPropagation();
                }}
              />
            </div>
            <div className="signup-widget-submit">
              {config.enableSignup && (
                <Button variant="primary" onClick={submitEmail}>
                  Next
                </Button>
              )}
            </div> */}
          </>
        )}
        {emailOK && !passwordOK && (
          <>
            <div className="signup-widget-name-container">
              <div className="signup-widget-name-part">
                <input
                  type="text"
                  value={firstName}
                  id="firstName"
                  placeholder="First name"
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    const value = event.target.value;
                    setFirstName(value);
                  }}
                  onKeyDown={(e: any) => {
                    // prevent annotation page keyboard commands from getting picked up
                    e.stopPropagation();
                  }}
                />
              </div>
              <div className="signup-widget-name-part">
                <input
                  type="text"
                  placeholder="Last name"
                  value={lastName}
                  id="lastName"
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    const value = event.target.value;
                    setLastName(value);
                  }}
                  onKeyDown={(e: any) => {
                    // prevent annotation page keyboard commands from getting picked up
                    e.stopPropagation();
                  }}
                />
              </div>
            </div>
            <div className="signup-widget-password">
              <form>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  placeholder="Enter a password"
                  onChange={handlePassword}
                  onKeyDown={(e: any) => {
                    // prevent annotation page keyboard commands from getting picked up
                    e.stopPropagation();
                  }}
                />
              </form>
            </div>
            <div className="signup-widget-submit">
              {config.enableSignup && (
                <Button variant="primary" onClick={handleVerifyEmail}>
                  Sign Up
                </Button>
              )}
            </div>
          </>
        )}
        {emailOK && passwordOK && (
          <>
            <div className="signup-widget-verify-container">
              <div className="signup-widget-verify-title">
                {/* <h1>Verify your account</h1> */}
              </div>
              <div className="signup-widget-verify-text">
                We’ve sent you an email with a 6 digit code. Enter that code to
                create your account. Your code will expire in 5 minutes.
              </div>
              <div className="signup-widget-verify-code">
                <input
                  type="text"
                  value={code}
                  id="email_verification_code"
                  placeholder="Enter 6 digit code from your email"
                  onChange={handleCode}
                  onKeyDown={(e: any) => {
                    // prevent annotation page keyboard commands from getting picked up
                    e.stopPropagation();
                  }}
                />
              </div>
              <div className="signup-widget-verify-confirm">
                <Button disabled={code.length !== 6} onClick={handleVerifyCode}>
                  Confirm
                </Button>
              </div>
              {codeErrorText && (
                <div className="signup-widget-verify-code-error">
                  {codeErrorText}
                </div>
              )}
              <div className="signup-widget-verify-resend">
                <a onClick={handleResend}>Re-send Verification Code</a>
              </div>
            </div>
          </>
        )}

        <div className="signup-widget-error">{errorText}</div>

        <div className="signup-widget-divider">&nbsp;</div>

        <div className="signup-widget-oauth">
          {config.enableSignup && (
            <button
              className="signup-widget-oauth-button"
              onClick={handleOAuthSignup}>
              <div className="signup-widget-button">
                <div className="signup-widget-button-logo">
                  <img className="icon-logo-google" />
                </div>
                <div className="signup-widget-button-text">
                  Create an account using Google
                </div>
              </div>
            </button>
          )}
        </div>

        <div className="signup-widget-text">
          Already have an account? <Link to="/login">Sign In</Link>.
        </div>

        <div className="signup-widget-disclaimer">
          By continuing with Google, you agree to Winslow's terms & Conditions
          and Privacy Policy
        </div>

        <div className="signup-widget-container"></div>
      </div>
      <Toaster />
    </>
  );
}
